import type { CargoByPort, CargoData, CargoSegment, ItineraryStop } from "../../shared/api/types";
import type { VoyageStopOverride } from "../../shared/api/endpoints";
import type { SegmentUnits } from "../../../../backend/shared";
import { formatDateTime, formatNumber } from "../../shared/locale";
import { segmentCode } from "../calendar/voyage-mapper";

export function fmtLocal(iso: string | null | undefined): string {
  return formatDateTime(iso);
}

// portFunc is a WW-internal single-letter code set — NOT an industry
// standard (UN/EDIFACT D.E. 8025 is the canonical call-purpose code
// list and it's numeric). Mapping confirmed by WW on 2026-05-22:
// L=Load, D=Discharge, A=Arrival pilot station, B=Departure pilot
// station, C=Commence voyage, T=Terminating, I=Canal transit,
// F=Fueling, P=Passing, W=Waiting, Y=Delivery, Z=Redelivery, R=Repair,
// O=Other. S is the only remaining unconfirmed code (not observed in
// cached itineraries either) — falls through to raw-letter rendering
// until WW IT confirms. See todo.md "Load / Discharge indicators
// don't always reflect reality" for the open work.
const PORT_FUNC_LABELS: Record<string, string> = {
  L: "Load",
  D: "Discharge",
  A: "Arrival pilot station",
  B: "Departure pilot station",
  C: "Commence voyage",
  T: "Terminating",
  I: "Canal transit",
  F: "Fueling",
  P: "Passing",
  W: "Waiting",
  Y: "Delivery",
  Z: "Redelivery",
  R: "Repair",
  O: "Other",
};

export function portFuncLabel(f: string | null | undefined): string {
  if (!f) return "Unknown";
  return PORT_FUNC_LABELS[f] ?? f;
}

// Symbols drawn on an itinerary stop's badge. A NORMAL stop shows its
// portFunc plus any additional L/D operations at the same call. A
// borrowed (DUMMY) stop shows only its additional L/D activities — its
// portFunc is the neighbouring voyage's operation, not the cross-voyage
// leg relevant here — falling back to portFunc when it has none so a
// borrowed stop is never blank. Order is primary-first, deduped, capped
// at 2 (a stop with three distinct symbols isn't seen in the data).
// Only "L"/"D" count as additional activities (upstream emits no others
// there); a malformed value is ignored.
export function stopSymbols(
  stop: Pick<ItineraryStop, "portFunc" | "isDummy" | "additionalActivities">,
): string[] {
  const addl = (stop.additionalActivities ?? []).filter((a) => a === "L" || a === "D");
  const raw = stop.isDummy
    ? (addl.length > 0 ? addl : (stop.portFunc ? [stop.portFunc] : []))
    : [stop.portFunc, ...addl];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const sym of raw) {
    if (!sym || seen.has(sym)) continue;
    seen.add(sym);
    out.push(sym);
    if (out.length === 2) break;
  }
  return out;
}

export interface ItineraryGroup {
  code: string;   // e.g. "BEANR"
  name: string;   // e.g. "ANTWERP"
  stops: ItineraryStop[];
}

export interface CargoSummary {
  segments: CargoSegment[];
  units: number;       // sum of qty
  cbm: number;         // sum of cbm
  weight: number;      // sum of weight (kg)
}

function summarise(rows: CargoByPort | undefined): CargoSummary | null {
  if (!rows || rows.details.length === 0) return null;
  let units = 0, cbm = 0, weight = 0;
  for (const d of rows.details) {
    units += d.qty || 0;
    cbm += d.cbm || 0;
    weight += d.weight || 0;
  }
  return { segments: rows.details, units, cbm, weight };
}

// cargoData keys are 5-char UN/LOCODEs that match the first 5 chars of an
// itinerary stop's externalRef — same prefix used by groupItineraries, so
// we can join cargo to grouped stops by code directly.
export function loadAt(cargo: CargoData | undefined, code: string): CargoSummary | null {
  return summarise(cargo?.loadByPort?.[code]);
}
export function dischargeAt(cargo: CargoData | undefined, code: string): CargoSummary | null {
  return summarise(cargo?.dischByPort?.[code]);
}

export function fmtCbm(cbm: number): string {
  // Round to whole m³ — segments often have fractional cbm but the sum
  // doesn't usually need decimals at the totals level.
  return `${formatNumber(Math.round(cbm))} m³`;
}
export function fmtUnits(n: number): string {
  const r = Math.round(n);
  return `${formatNumber(r)} unit${Math.abs(r) === 1 ? "" : "s"}`;
}
export function fmtWeight(kg: number): string {
  // Tonnes for readability — values run from a few hundred kg to millions.
  const t = kg / 1000;
  return `${formatNumber(t, { maximumFractionDigits: 1 })} t`;
}

// Which itinerary stop(s) the currently-selected calendar bar represents.
// Schedule API emits per-call entries when a vessel visits the same
// terminal twice in one voyage, so portName-only matching lights up both
// stops and opens both <details> groups. Selection carries the bar's
// whole-port-stay [arrivalLocal, departureLocal] window; the per-terminal
// stop's window is contained within it, so containment narrows to the
// correct one. Falls back to all matching-portName stops when:
//
//   - No window is provided (vessels-tab selection has no per-call timing)
//   - Window strings are unparseable
//   - No stop falls inside the window (data inconsistency — schedule and
//     details windows disagree per CLAUDE.md's "never match terminal
//     calls by time across the two APIs", containment can occasionally
//     yield empty)
//
// Returned set is keyed by `ItineraryStop.order` so callers can membership-
// test per stop without re-running the matching.
export function pickHighlightedStopOrders(
  stops: readonly ItineraryStop[],
  ctx: { portName: string | null; arrivalLocal?: string | null | undefined; departureLocal?: string | null | undefined },
): Set<number> {
  if (!ctx.portName) return new Set();
  // Skip dummy anchor stops — they belong to neighbouring voyages and
  // share `order` with a real stop on this voyage. Without this skip
  // the call site's `highlighted().has(stop.order)` membership check
  // would light up both the real stop and the dummy that share the
  // boundary order. Callers also still gate on `!stop.isDummy` for
  // belt-and-braces.
  const byName = stops.filter((s) => s.portName === ctx.portName && !s.isDummy);
  if (byName.length === 0) return new Set();
  const winStart = ctx.arrivalLocal ? Date.parse(ctx.arrivalLocal) : NaN;
  const winEnd = ctx.departureLocal ? Date.parse(ctx.departureLocal) : NaN;
  if (!Number.isFinite(winStart) || !Number.isFinite(winEnd)) {
    return new Set(byName.map((s) => s.order));
  }
  const contained = byName.filter((s) => {
    if (!s.arrivalLocal || !s.departureLocal) return false;
    const a = Date.parse(s.arrivalLocal);
    const d = Date.parse(s.departureLocal);
    if (!Number.isFinite(a) || !Number.isFinite(d)) return false;
    return a >= winStart && d <= winEnd;
  });
  if (contained.length === 0) return new Set(byName.map((s) => s.order));
  return new Set(contained.map((s) => s.order));
}

// Groups consecutive itinerary stops that share the same 5-letter externalRef
// prefix (UN/LOCODE country+city without the terminal suffix). Same algorithm
// as the OLD app's groupItineraries.
export function groupItineraries(stops: ItineraryStop[]): ItineraryGroup[] {
  const out: ItineraryGroup[] = [];
  let current: ItineraryGroup | null = null;
  for (const stop of stops) {
    const ref = stop.port?.externalRef ?? "";
    const code = ref.length >= 5 ? ref.slice(0, 5) : `?${stop.portName ?? ""}`;
    if (!current || current.code !== code) {
      const parts = (stop.portName ?? code).split(/\s+-\s+/);
      const nameValue: string = (parts[0] ?? "").trim() || code;
      current = { code, name: nameValue, stops: [] };
      out.push(current);
    }
    current.stops.push(stop);
  }
  return out;
}

// One-line summary of an orphaned override's saved content, shown in the
// itinerary callout when it has no stevedore sessions to render inline
// (either a volume-only row, or a row with sessions that renders each
// window individually via formatSession instead).
export function sessionSummary(o: VoyageStopOverride): string {
  const n = o.stevedoreSessions?.length ?? 0;
  if (!n) return "volume override";
  const days = o.stevedoreSessions!.map((sess) => sess.start.slice(5, 10)).join(", ");
  return `${n} stevedore window${n === 1 ? "" : "s"} · ${days}`;
}

// Full detail of one stevedore window, e.g. "07 Jul 06:00 → 07 Jul
// 22:00" or, when it spans days, "07 Jul 22:00 → 08 Jul 14:00". Always
// shows both dates (matches the override dialog's date -> date input
// layout) even when start and end share a day.
export function formatSession(sess: { start: string; end: string }): string {
  const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const day = (iso: string) => `${iso.slice(8, 10)} ${MON[Number(iso.slice(5, 7)) - 1] ?? "?"}`;
  const time = (iso: string) => iso.slice(11, 16);
  return `${day(sess.start)} ${time(sess.start)} → ${day(sess.end)} ${time(sess.end)}`;
}

const SEGMENT_UNIT_CODES = ["C", "HH", "RT", "BB", "NCC"] as const;

// Sum a CargoSummary's per-segment volumes into the canonical
// SegmentUnits shape ({ C, HH, RT, BB, NCC }) used by the override
// dialog's per-segment grid. Returns null when no segment falls into a
// known code, so the dialog shows an empty placeholder.
export function segmentSummary(sum: CargoSummary | null): SegmentUnits | null {
  if (!sum) return null;
  const out: SegmentUnits = {};
  let any = false;
  for (const seg of sum.segments) {
    const code = segmentCode(seg.segment);
    if (!(SEGMENT_UNIT_CODES as readonly string[]).includes(code)) continue;
    out[code as (typeof SEGMENT_UNIT_CODES)[number]] = (out[code as (typeof SEGMENT_UNIT_CODES)[number]] ?? 0) + Math.round(seg.qty || 0);
    any = true;
  }
  return any ? out : null;
}

// Aggregate per-segment overrides across all stops in a port-group for
// one side (load / discharge). Returns null when no stop in the group
// has overrides for that side. When a code IS overridden by any stop,
// the sum of those overrides REPLACES the upstream value for that code
// at the group level — a simplification that works well for single-stop
// groups (the common case) and gives a sensible aggregate for the rare
// multi-stop case.
export function aggregateOverride(
  stops: readonly ItineraryStop[],
  overrideForStop: (stop: ItineraryStop) => { loadSegments?: SegmentUnits | null; dischargeSegments?: SegmentUnits | null } | null,
  side: "load" | "discharge",
): SegmentUnits | null {
  let out: SegmentUnits | null = null;
  for (const stp of stops) {
    const ov = overrideForStop(stp);
    const segs = side === "load" ? ov?.loadSegments : ov?.dischargeSegments;
    if (!segs) continue;
    if (!out) out = {};
    for (const code of SEGMENT_UNIT_CODES) {
      const v = segs[code];
      if (typeof v === "number" && Number.isFinite(v)) {
        out[code] = (out[code] ?? 0) + v;
      }
    }
  }
  return out;
}

// "Effective" units total once group-level overrides are applied on top
// of the upstream `summary`. For each canonical code the override (if
// present) replaces the upstream qty; codes without an override keep
// their upstream value. Returns null when nothing is overridden so
// callers render the unchanged-by-default UI. cbm + weight are
// deliberately NOT recomputed — unit overrides don't scale 1:1 with
// volume / weight.
export interface EffectiveTotals {
  units: number;
  originalUnits: number;
}
export function effectiveSummary(
  summary: CargoSummary | null,
  override: SegmentUnits | null,
): EffectiveTotals | null {
  if (!summary || !override) return null;
  const upstreamByCode: Record<string, number> = {};
  for (const seg of summary.segments) {
    const code = segmentCode(seg.segment);
    upstreamByCode[code] = (upstreamByCode[code] ?? 0) + (seg.qty || 0);
  }
  let units = 0;
  const seenCodes = new Set<string>();
  for (const code of SEGMENT_UNIT_CODES) {
    const ovQty = override[code];
    if (typeof ovQty !== "number") continue;
    seenCodes.add(code);
    units += ovQty;
  }
  for (const [code, q] of Object.entries(upstreamByCode)) {
    if (!seenCodes.has(code)) units += q;
  }
  return { units: Math.round(units), originalUnits: Math.round(summary.units) };
}
